import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, RotateCcw, Play, Pause, MousePointer, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const carImages = [
  "/assets/car1.webp",
  "/assets/car5.webp",
  "/assets/car6.webp",
  "/assets/car7.webp",
  "/assets/car8.webp",
  "/assets/car9.webp",
  "/assets/car10.webp",
  "/assets/car2.webp",
  "/assets/car3.webp",
  "/assets/car4.webp",
  
  "/assets/car5.webp",
  "/assets/car6.webp",
  "/assets/car7.webp",
  "/assets/car8.webp",
  "/assets/car9.webp",
  "/assets/car10.webp",
  "/assets/car1.webp",
  "/assets/car2.webp",
  "/assets/car3.webp",
  "/assets/car4.webp",
  "/assets/car5.webp",
  "/assets/car6.webp",
  "/assets/car7.webp",
  "/assets/car5.webp",
  "/assets/car6.webp",
  "/assets/car7.webp",
  "/assets/car8.webp",
  "/assets/car9.webp",
  "/assets/car10.webp",
  "/assets/car8.webp",
  "/assets/car9.webp",
  "/assets/car10.webp",
  "/assets/car1.webp",
  "/assets/car2.webp",
  "/assets/car3.webp",
  "/assets/car4.webp",
  "/assets/car5.webp",
  "/assets/car6.webp",
  "/assets/car7.webp",
  "/assets/car8.webp",
  "/assets/car9.webp",
  "/assets/car10.webp",
];

const Premium360Viewer = ({ viewMode = 'desktop' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const isDragging = useRef(false);
  const lastX = useRef(0);
  const autoRotateInterval = useRef(null);

  useEffect(() => {
    // Simulate loading with staggered effect
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isAutoRotating) {
      autoRotateInterval.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % carImages.length);
      }, 400); // Slower rotation for better viewing (400ms per frame)
    } else {
      clearInterval(autoRotateInterval.current);
    }

    return () => clearInterval(autoRotateInterval.current);
  }, [isAutoRotating]);

  const handleMouseDown = (e) => {
    if (isAutoRotating) setIsAutoRotating(false);
    setHasInteracted(true);
    isDragging.current = true;
    lastX.current = e.clientX;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;

    const deltaX = e.clientX - lastX.current;
    const sensitivity = 3; // Reduce sensitivity for smoother control

    if (Math.abs(deltaX) > sensitivity) {
      const nextIndex = (currentIndex + (deltaX > 0 ? 1 : -1) + carImages.length) % carImages.length;
      setCurrentIndex(nextIndex);
      lastX.current = e.clientX;
    }
  };

  const handleTouchStart = (e) => {
    if (isAutoRotating) setIsAutoRotating(false);
    setHasInteracted(true);
    isDragging.current = true;
    lastX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();

    const deltaX = e.touches[0].clientX - lastX.current;
    const sensitivity = 3;

    if (Math.abs(deltaX) > sensitivity) {
      const nextIndex = (currentIndex + (deltaX > 0 ? 1 : -1) + carImages.length) % carImages.length;
      setCurrentIndex(nextIndex);
      lastX.current = e.touches[0].clientX;
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
  };

  const resetRotation = () => {
    setCurrentIndex(0);
    setIsAutoRotating(true);
  };

  return (
    <div className="w-full h-96 flex flex-col  relative overflow-hidden">
      {/* Animated background */}
      {/* <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-cyan-500/5 animate-pulse"></div> */}
      
      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/30 rounded-full animate-bounce" style={{animationDelay: '0s'}}></div>
        <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-purple-400/30 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/4 left-3/4 w-1.5 h-1.5 bg-cyan-400/30 rounded-full animate-bounce" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main viewer area */}
      <div 
        className={`flex-1 relative ${isDragging.current ? 'cursor-grabbing' : 'cursor-grab'} flex items-center justify-center p-6`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isLoading ? (
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-blue-500/30 rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-20 h-20 border-4 border-t-blue-500 border-r-purple-500 border-b-cyan-500 border-l-transparent rounded-full animate-spin"></div>
            </div>
            <div className="text-center">
              <p className="text-gray-300 text-lg font-medium mb-2">Loading Premium Experience</p>
              <div className="flex items-center justify-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Main image container */}
            
            <div className="relative group">

         
              {/* <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-700"></div> */}
              
              <div className="mx-auto w-full max-w-md h-[200px]  overflow-hidden ">
  <video
    autoPlay
    muted
    loop
    playsInline
    className="w-full h-full object-cover"
  >
    <source src="/assets/moller1.mp4" type="video/mp4" />
  </video>
</div>
            </div>
              {/* Sidebar placeholders */}
           
           
          </>
        )}
      </div>
    </div>
  );
};

function MilesDetail() {
  const [isMobile, setIsMobile] = useState(true);

  return (
    <>
    <Helmet>
           <title>
    Möller’s Campaign | Video Ad Showcase by Bidbox
            </title>
            <meta
              name="description"
              content="See how Möller’s brings stories to life with Video ads on Bidbox. Explore impactful campaigns that build trust & connect with health-conscious audiences"
            />
            {/* <meta
              name="keywords"
              content="BidBox AI, Demand-Side Platform, DSP solutions, targeted advertising, ad campaign optimization, programmatic advertising, real-time bidding, ad tech, audience segmentation, AI-driven advertising, digital marketing, performance marketing, cross-channel advertising, ad exchange, ad placement, ad inventory, ad network, data-driven marketing, media buying."
            /> */}
             <link
              rel="canonical"
              href="https://bidbox.ai/video/mollers"
            />
            <meta
              name="robots"
              content="max-snippet:-1, max-image-preview:standard"
            />
            <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
    <div className="min-h-screen bg-gray-50 mt-10">
      <div className="container mx-auto px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-12">
          {/* Left column - Content */}
          <div className="flex-1 lg:max-w-lg">
            {/* Header */}
            <header className="animate-slide-up">
              {/* Title and tag */}
              <div className="mb-4 lg:mt-16 md:mt-10 sm:mt-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  Moller’s: Watch. Engage. Remember.
                  </h1>
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Video
                  </span>
                </div>
              </div>
            </header>

            {/* Content Section */}
            <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {/* Make it yours section */}
              <div className="mb-8">
                <h2 className="text-lg font-bold text-blue-600 mb-4 tracking-wide">
              
                DELIVER YOUR MESSAGE WITH IMPACT
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6 max-w-md">
                This interactive video ad puts your product story front and center. With smooth playback, responsive layout, and eye-catching visuals, it draws users in from the first frame. Just drop in your brand video and messaging to launch a high-impact format that connects, captivates, and converts.

                </p>
                
                {/* CTA Button */}
                <Link to='/contact'>
                <button className="group bg-white border-2 border-blue-500 text-blue-500 px-6 py-3 rounded-full font-medium hover:bg-blue-500 hover:text-white transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md transform hover:-translate-y-0.5">
                  <span>Start with this Template</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right column - Template Preview */}
          <div className="flex-1 lg:max-w-2xl mt-8 lg:mt-0">
            {/* Preview toggle */}
            <div className="hidden lg:flex justify-center mt-2 animate-fade-in mb-4" style={{ animationDelay: '0.3s' }}>
                <div className="bg-white rounded-full px-6 py-3 shadow-lg border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium transition-colors ${isMobile ? 'text-blue-600' : 'text-gray-600'}`}>
                        Mobile
                      </span>
                      <button
                        onClick={() => setIsMobile(!isMobile)}
                        className="relative w-12 h-6 bg-blue-500 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${isMobile ? 'left-1' : 'left-7'}`}></div>
                      </button>
                      <span className={`text-sm font-medium transition-colors ${!isMobile ? 'text-blue-600' : 'text-gray-600'}`}>
                        Desktop
                      </span>
                    </div>
                  </div>
                </div>
              </div>
           
            <div className="relative animate-fade-in" style={{ animationDelay: '0.4s' }}>
              
              {/* Mobile Mockup */}
              {isMobile && (
                <div className="mx-auto max-w-xs">
                  {/* iPhone mockup */}
                  <div className="relative bg-gray-200 rounded-[2.5rem]  shadow-2xl">
                    {/* Status bar area */}
                    <div className="bg-white rounded-[2rem] overflow-hidden">
                      {/* Status bar */}
                      <div className="bg-white px-6 py-2 flex justify-between items-center text-black text-sm font-medium">
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                          <div className="flex space-x-1">
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                          </div>
                          <div className="w-6 h-3 border border-black rounded-sm relative">
                            <div className="w-4/5 h-full bg-green-500 rounded-sm"></div>
                          </div>
                        </div>
                      </div>

                      {/* Browser bar */}
                      <div className="bg-gray-100 px-4 py-3 flex items-center space-x-3">
                        <div className="flex-1 bg-white rounded-lg px-3 py-2 text-sm text-gray-600 flex items-center justify-center">
                        🔒 moller.com
                        </div>
                      </div>

                      {/* Content area */}
                      <div className=" bg-gray-50 h-[400px] overflow-y-auto hide-scrollbar">
                        {/* 360 Viewer - Full screen on mobile */}
                        <div className="space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                  </div>
                  <div className="mb-1">
                  <div className=" w-80 relative group transform transition-all duration-300 rounded-lg overflow-hidden">
                          <Premium360Viewer viewMode="mobile" />
                          

                        </div>
                        </div>
                        <div className="space-y-3">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        
                      </div>
                      
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop Mockup */}
              {!isMobile && (
                <div className="w-full max-w-4xl mx-auto hidden lg:block">
                  {/* Desktop Screen Frame */}
                  <div className="bg-gray-200 rounded-xl p-3 shadow-2xl">
                    {/* Screen */}
                    <div className="bg-white rounded-lg overflow-hidden shadow-inner" style={{ aspectRatio: '16/10' }}>
                      {/* macOS-style window header */}
                      <div className="bg-gray-100 px-4 py-3 flex items-center border-b border-gray-200">
                        <div className="flex space-x-2">
                          <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="flex-1 mx-2">
                          <div className="bg-white rounded-md px-2 py-1 text-sm text-gray-600 border shadow-sm max-w-md">
                            🔒 moller.com
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                        </div>
                      </div>

                      {/* Content area */}
                      {/* <div className="p-4 bg-gray-50 min-h-[100px]"> */}
                  
                      {/* Browser content area */}
                      <div className="bg-gray-50 h-[500px] overflow-y-auto hide-scrollbar">
                        {/* 360 Viewer centered on desktop */}
                        <div className="space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                  </div>
                  
                        <div className="flex justify-center ">
                          <div className="w-full max-w-xs ">
                            <Premium360Viewer viewMode="desktop" />
                          </div>
                        {/* </div> */}
                      </div>
                       {/* Additional content placeholders */}
                       <div className="grid grid-cols-2 gap-6 mb-6 mt-2">
                            <div className="space-y-3">
                              <div className="h-4 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                            </div>
                            <div className="space-y-3 mt-2">
                              <div className="h-4 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                            </div>
                          </div>

                          {/* Sidebar placeholders */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                          </div>
                          <div className="grid grid-cols-2 gap-6 mb-6">
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                          </div>
                        {/* Sidebar placeholders */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="h-24 bg-gray-200 rounded"></div>
                          <div className="h-24 bg-gray-200 rounded"></div>
                          <div className="h-24 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                    </div>
                  </div>
                  
                  {/* Monitor stand */}
                  <div className="flex justify-center">
                    <div className="w-20 h-4 bg-gray-200 rounded-b-lg"></div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-32 h-2 bg-gray-300 rounded-full"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
                    <h1 className="text-3xl font-bold text-center text-gray-800 mb-10">Discover More Ads</h1>
              
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {/* Card 1 */}
                      <Link to="/quiz/ramadan" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                        <img
                          src="/assets/rama.webp"
                          alt="Card 1"
                          className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <h2 className="text-lg font-semibold">Ramadan</h2>
                        </div>
                      </Link>
              
                      {/* Card 2 */}
                      <Link to="/dynamic-calculator/zakat" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                        <img
                          src="/assets/zakat.webp"
                          alt="Card 2"
                          className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <h2 className="text-lg font-semibold">Zakat Calculator</h2>
                        </div>
                      </Link>
              
                      {/* Card 3 */}
                      <Link to="/interactive-selector/donation" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                        <img
                          src="/assets/donation.png "
                          alt="Card 3"
                          className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <h2 className="text-lg font-semibold">Donation</h2>
                        </div>
                      </Link>
              
                      {/* Card 4 */}
                      <Link to="/spin-the-wheel/mamaearth" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                        <img
                          src="/assets/mam1.jpg"
                          alt="Card 4"
                          className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                          <h2 className="text-lg font-semibold">Mama Earth</h2>
                        </div>
                      </Link>
              
                     
                    </div>
                  </div>
    </>
  );
}

export default MilesDetail;




// import React, { useState } from 'react';
// import { ChevronLeft, ChevronRight, Smartphone, Monitor, QrCode } from 'lucide-react';

// const MilesDetail = () => {
//   const [selectedAdId, setSelectedAdId] = useState(2);
//   const [viewMode, setViewMode] = useState('desktop'); // 'mobile' or 'desktop'

//   const adFormats = [
//     {
//       id: 1,
//       name: '2025_05_...',
//       dimension: '300x250',
//       type: 'Banner - Javascript tag',
//       creativeId: '1552639'
//     },
//     {
//       id: 2,
//       name: '2025_05...',
//       dimension: '300x600',
//       type: 'Banner - Javascript tag',
//       creativeId: '1552640'
//     },
//     {
//       id: 3,
//       name: '2025_05...',
//       dimension: '320x480',
//       type: 'Banner - Javascript tag',
//       creativeId: '1552641'
//     }
//   ];

//   const currentAd = adFormats.find(ad => ad.id === selectedAdId) || adFormats[1];

//   const selectAd = (adId) => {
//     setSelectedAdId(adId);
//   };

//   const nextAd = () => {
//     const currentIndex = adFormats.findIndex(ad => ad.id === selectedAdId);
//     const nextIndex = currentIndex === adFormats.length - 1 ? 0 : currentIndex + 1;
//     setSelectedAdId(adFormats[nextIndex].id);
//   };

//   const prevAd = () => {
//     const currentIndex = adFormats.findIndex(ad => ad.id === selectedAdId);
//     const prevIndex = currentIndex === 0 ? adFormats.length - 1 : currentIndex - 1;
//     setSelectedAdId(adFormats[prevIndex].id);
//   };

//   const AdContent = () => {
//     return (
//       <div className="relative">
//         {currentAd.dimension === '300x250' && (
//           <div className="w-full max-w-[300px] mx-auto">
            // <div className="relative w-[300px] h-[250px] rounded-lg overflow-hidden shadow-lg">
            //   <video
            //     autoPlay
            //     muted
            //     loop
            //     playsInline
            //     className="w-full h-full object-cover"
            //   >
            //     <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4" type="video/mp4" />
            //     {/* Fallback gradient background */}
            //     <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-red-600 to-orange-600 flex items-center justify-center">
            //       <div className="text-white text-center">
            //         <div className="text-2xl mb-2">✈️</div>
            //         <div className="text-lg font-bold">Turkish Airlines</div>
            //         <div className="text-sm">Your browser doesn't support video</div>
            //       </div>
            //     </div>
            //   </video>
            // </div>
//           </div>
//         )}
        
//         {currentAd.dimension === '300x600' && (
//           <div className="w-full max-w-[300px] mx-auto">
//             <div className="relative w-[300px] h-[600px] rounded-lg overflow-hidden shadow-lg">
//               <video
//                 autoPlay
//                 muted
//                 loop
//                 playsInline
//                 className="w-full h-full object-cover"
//               >
//                 <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4" type="video/mp4" />
//                 {/* Fallback gradient background */}
//                 <div className="absolute inset-0 bg-gradient-to-b from-cyan-300 via-sky-400 to-blue-600 flex items-center justify-center">
//                   <div className="text-white text-center">
//                     <div className="text-3xl mb-2">✈️</div>
//                     <div className="text-xl font-bold">Turkish Airlines</div>
//                     <div className="text-sm">Your browser doesn't support video</div>
//                   </div>
//                 </div>
//               </video>
//             </div>
//           </div>
//         )}
        
//         {currentAd.dimension === '320x480' && (
//           <div className="w-full max-w-[320px] mx-auto">
//             <div className="relative w-[320px] h-[480px] rounded-lg overflow-hidden shadow-lg">
//               <video
//                 autoPlay
//                 muted
//                 loop
//                 playsInline
//                 className="w-full h-full object-cover"
//               >
//                 <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4" type="video/mp4" />
//                 {/* Fallback gradient background */}
//                 <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center">
//                   <div className="text-white text-center">
//                     <div className="text-3xl mb-2">✈️</div>
//                     <div className="text-xl font-bold">Turkish Airlines</div>
//                     <div className="text-sm">Your browser doesn't support video</div>
//                   </div>
//                 </div>
//               </video>
//             </div>
//           </div>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="min-h-screen bg-gray-50 flex mt-12 overflow-hidden">
//       {/* Left Sidebar */}
//       <div className="w-64 bg-white border-r border-gray-200 p-4 hidden lg:block">
//         <div className="mb-6">
//           <h2 className="text-lg font-semibold text-gray-800 mb-2">2025_05_..</h2>
//         </div>

//         <div className="mb-6">
//           <button className="text-sm text-blue-500 hover:underline">All creatives preview</button>
//         </div>

//         <div className="space-y-2">
//           {adFormats.map((ad) => (
//             <div 
//               key={ad.id}
//               onClick={() => selectAd(ad.id)}
//               className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
//                 ad.id === selectedAdId
//                   ? 'bg-blue-100 border-blue-300 shadow-sm' 
//                   : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
//               }`}
//             >
//               <div className="text-sm font-medium text-gray-800 mb-2 truncate">
//                 {ad.name}
//               </div>
              
//               {/* Ad Preview Thumbnail */}
//               <div className="mb-3 flex justify-center">
//                 {ad.dimension === '300x250' && (
//                   <div className="w-20 h-16 bg-gradient-to-br from-red-400 to-orange-500 rounded border shadow-sm flex items-center justify-center">
//                     <div className="text-xs text-white font-bold">📹</div>
//                   </div>
//                 )}
//                 {ad.dimension === '300x600' && (
//                   <div className="w-12 h-24 bg-gradient-to-b from-cyan-300 to-blue-400 rounded border shadow-sm flex items-center justify-center">
//                     <div className="text-xs text-white font-bold">📹</div>
//                   </div>
//                 )}
//                 {ad.dimension === '320x480' && (
//                   <div className="w-16 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded border shadow-sm flex items-center justify-center">
//                     <div className="text-xs text-white font-bold">📹</div>
//                   </div>
//                 )}
//               </div>
              
//               <div className="space-y-1">
//                 <div className="text-xs text-blue-500">
//                   <span className="font-medium">Ad dimension:</span> {ad.dimension}
//                 </div>
//                 <div className="text-xs text-gray-600">
//                   <span className="font-medium">Ad type:</span> {ad.type}
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>

//       {/* Main Content */}
//       <div className="flex-1 flex flex-col">
//         {/* Header */}
//         <div className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4 flex items-center justify-between">
//           <div className="flex items-center space-x-2 lg:space-x-4">
//             <h1 className="text-lg lg:text-xl font-semibold text-gray-800 truncate">
//               <span className="hidden sm:inline">2025_05_Video_300...</span>
//               <span className="sm:hidden">bidbox.ai</span>
//             </h1>
//             <div className="w-2 h-2 bg-gray-400 rounded-full hidden sm:block"></div>
//           </div>
          
//           <div className="flex items-center space-x-2 lg:space-x-4">
//             <div className="flex items-center bg-gray-100 rounded-lg p-1">
//               <button
//                 onClick={() => setViewMode('mobile')}
//                 className={`flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm transition-colors ${
//                   viewMode === 'mobile' 
//                     ? 'bg-purple-600 text-white' 
//                     : 'text-gray-600 hover:bg-gray-200'
//                 }`}
//               >
//                 <Smartphone size={14} />
//                 <span className="hidden sm:inline">Mobile</span>
//               </button>
//               <button
//                 onClick={() => setViewMode('desktop')}
//                 className={`flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-2 rounded-md text-xs lg:text-sm transition-colors ${
//                   viewMode === 'desktop' 
//                     ? 'bg-gray-800 text-white' 
//                     : 'text-gray-600 hover:bg-gray-200'
//                 }`}
//               >
//                 <Monitor size={14} />
//                 <span className="hidden sm:inline">Desktop</span>
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Content Area */}
//         <div className="flex-1 flex flex-col lg:flex-row">
//           {/* Ad Preview */}
//           <div className="flex-1 p-2 lg:p-6">
//             {/* Navigation Buttons */}
//             <div className="flex items-center justify-center space-x-4 lg:space-x-6 mb-4 lg:mb-6">
//               <button 
//                 onClick={prevAd}
//                 className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
//               >
//                 <ChevronLeft size={18} />
//                 <span className="hidden sm:inline">Prev Ad</span>
//                 <span className="sm:hidden">Prev</span>
//               </button>
              
//               <div className="text-xs lg:text-sm text-gray-500 font-medium">
//                 {adFormats.findIndex(ad => ad.id === selectedAdId) + 1} of {adFormats.length}
//               </div>
              
//               <button 
//                 onClick={nextAd}
//                 className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
//               >
//                 <span className="hidden sm:inline">Next Ad</span>
//                 <span className="sm:hidden">Next</span>
//                 <ChevronRight size={18} />
//               </button>
//             </div>

//             {/* Ad Container */}
//             <div className="w-full max-w-6xl mx-auto">
//               {viewMode === 'mobile' ? (
//                 // Mobile Phone Mockup
//                 <div className="max-w-sm mx-auto">
//                   <div className="bg-gray-800 rounded-3xl p-2 shadow-2xl">
//                     {/* Phone Screen */}
//                     <div className="bg-white rounded-2xl overflow-hidden" style={{aspectRatio: '9/19.5'}}>
//                       {/* Status Bar */}
//                       <div className="bg-white px-4 py-2 flex justify-between items-center text-xs font-semibold">
//                         <span>10:59 AM</span>
//                         <div className="flex items-center space-x-1">
//                           <span className="text-xs">5G</span>
//                           <div className="flex space-x-0.5">
//                             <div className="w-1 h-3 bg-black rounded-full"></div>
//                             <div className="w-1 h-3 bg-black rounded-full"></div>
//                             <div className="w-1 h-3 bg-black rounded-full"></div>
//                             <div className="w-1 h-2 bg-gray-300 rounded-full"></div>
//                           </div>
//                           <div className="w-6 h-3 border border-black rounded-sm">
//                             <div className="w-4 h-1.5 bg-black rounded-sm m-0.5"></div>
//                           </div>
//                         </div>
//                       </div>
                      
//                       {/* App Header */}
//                       <div className="bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-between">
//                         <div className="text-blue-600 font-bold text-lg">bidbox.ai</div>
//                         <div className="w-6 h-6 flex flex-col justify-center space-y-1">
//                           <div className="w-full h-0.5 bg-gray-600"></div>
//                           <div className="w-full h-0.5 bg-gray-600"></div>
//                           <div className="w-full h-0.5 bg-gray-600"></div>
//                         </div>
//                       </div>
                      
//                       {/* Content Area */}
//                       <div className="bg-gray-50 p-4 flex-1 flex items-center justify-center">
//                         {/* Video Ad */}
//                         <div className="bg-white rounded-lg p-2 shadow-sm">
//                           <div className="text-xs text-gray-500 text-center mb-2">Video Advertisement</div>
//                           <AdContent />
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               ) : (
//                 // Desktop View
//                 <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-4xl mx-auto">
//                   <div className="p-8 flex items-center justify-center">
//                     {/* Video Ad - Highlighted */}
//                     <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50/30">
//                       <div className="text-xs text-blue-600 text-center mb-2 font-medium">Video Advertisement</div>
//                       <AdContent />
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Right Sidebar */}
//           <div className="w-80 bg-white border-l border-gray-200 p-6 hidden xl:block">
//             <h3 className="text-lg font-semibold text-gray-800 mb-6">Video Creative Details</h3>
            
//             <div className="space-y-6">
//               <div className="bg-purple-50 rounded-lg p-4">
//                 <h4 className="font-medium text-purple-800 mb-3">Video Ad Info</h4>
//                 <div className="space-y-2 text-sm">
//                   <div>
//                     <span className="font-medium text-gray-700">Ad Dimension:</span>
//                     <div className="text-gray-600">{currentAd.dimension}</div>
//                   </div>
//                   <div>
//                     <span className="font-medium text-gray-700">Ad Type:</span>
//                     <div className="text-gray-600">Video - Autoplay</div>
//                   </div>
//                   <div>
//                     <span className="font-medium text-gray-700">Creative ID:</span>
//                     <div className="text-gray-600">{currentAd.creativeId}</div>
//                   </div>
//                   <div>
//                     <span className="font-medium text-gray-700">Video Properties:</span>
//                     <div className="text-gray-600 text-xs mt-1">
//                       <div>• Autoplay: Enabled</div>
//                       <div>• Muted: Yes</div>
//                       <div>• Loop: Enabled</div>
//                       <div>• Plays Inline: Yes</div>
//                     </div>
//                   </div>
//                 </div>
//                 <button className="text-blue-500 text-sm hover:underline mt-3">
//                   View Full-Size Video
//                 </button>
//               </div>

//               <div className="flex justify-center">
//                 <div className="w-32 h-32 border border-gray-300 rounded-lg flex items-center justify-center bg-white">
//                   <QrCode size={80} className="text-gray-400" />
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Mobile Menu Button */}
//       <div className="lg:hidden fixed bottom-4 right-4">
//         <button className="bg-blue-600 text-white p-3 rounded-full shadow-lg">
//           <Monitor size={24} />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default MilesDetail;


